// src/components/rundown/RundownSettingsPanel.tsx
// Settings → Rundown: the Resource Rundown's band thresholds, Standard Monthly
// Hours, Win detection threshold (spec RR-21.2) and per-Discipline Billable
// Target defaults (RR-21.1). RM-admin only — the route is gated on
// `configureRundownSettings` (spec §5).
//
// Terminology: this screen is about UTILIZATION (hours). It never says
// "capacity", which in Nymbl Staff means points (spec §8). "Variance" appears
// once, as a tooltip on Billable Gap, which is the only sanctioned use.
//
// Saving thresholds creates a NEW settings version and leaves every published
// edition on the version it was built under — the panel says so, because an
// admin who thinks they are editing one shared number will not expect that.
import { useEffect, useState } from "react";
import { NonDeliveryHomeRolesSection } from "@/components/rundown/NonDeliveryHomeRolesSection";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type RundownSettingsDraft,
  useRundownConfig,
  useUpdateDisciplineDefaults,
  useUpdateRundownSettings,
} from "@/hooks/use-rundown-config";

interface ThresholdField {
  key: keyof RundownSettingsDraft;
  label: string;
  hint: string;
  unit: string;
}

/**
 * The Availability report's colour cut-offs, as a share of Available Hours Booked.
 * They sit beside the Over-utilized threshold because the four together draw the
 * report's bands; the server rejects values that are out of order.
 */
const AVAILABILITY_FIELDS: ThresholdField[] = [
  {
    key: "someRoomFloorPct",
    label: "Some room floor",
    hint: "Booked % at or above this is Some room; below it is Available.",
    unit: "%",
  },
  {
    key: "fullyBookedFloorPct",
    label: "Fully booked floor",
    hint: "Booked % at or above this is Fully booked.",
    unit: "%",
  },
  {
    key: "stretchedThresholdPct",
    label: "Stretched threshold",
    hint: "Booked % at or above this is Stretched. It must sit below the Over-utilized threshold.",
    unit: "%",
  },
];

const THRESHOLD_FIELDS: ThresholdField[] = [
  {
    key: "overThresholdPct",
    label: "Over-utilized threshold",
    hint: "Total Billable % at or above this puts a Resource in Over-utilized.",
    unit: "%",
  },
  {
    key: "activeCeilingPct",
    label: "Active ceiling",
    hint: "Active Billable % above this puts a Resource in Over-utilized, whatever the total.",
    unit: "%",
  },
  {
    key: "gapThresholdPts",
    label: "Gap threshold",
    hint: "Billable Gap at or above this makes a Resource an exception. Shown as “variance” in some reports.",
    unit: "pts",
  },
  {
    key: "proposedDependentTolerancePts",
    label: "Proposed-Dependent tolerance",
    hint: "How far below target Total Billable % may sit and still count as Proposed-Dependent.",
    unit: "pts",
  },
  {
    key: "winThresholdHours",
    label: "Guidance Win threshold",
    hint: "Monthly guidance increase that makes a Guidance Win candidate.",
    unit: "hrs/mo",
  },
  {
    key: "standardMonthlyHours",
    label: "Standard Monthly Hours",
    hint: "Hours in a full month before approved time off is deducted.",
    unit: "hrs",
  },
];

export function RundownSettingsPanel() {
  const { data, isLoading, isError, error, refetch } = useRundownConfig();
  const saveSettings = useUpdateRundownSettings();
  const saveDefaults = useUpdateDisciplineDefaults();

  const [draft, setDraft] = useState<RundownSettingsDraft | null>(null);
  const [defaults, setDefaults] = useState<Record<string, string>>({});

  // Seed the editable copies once the server values arrive, and re-seed after a
  // save so the inputs show what was actually stored rather than what was typed.
  useEffect(() => {
    if (!data) return;
    const { version: _version, ...rest } = data.settings;
    setDraft(rest);
    setDefaults(
      Object.fromEntries(
        data.disciplineDefaults.map((d) => [
          d.disciplineId,
          d.targetPct === null ? "" : String(d.targetPct),
        ]),
      ),
    );
  }, [data]);

  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : "Failed to load Rundown settings"}
        onRetry={() => refetch()}
      />
    );
  }

  if (isLoading || !data || !draft) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const onThresholdChange = (key: keyof RundownSettingsDraft, raw: string) => {
    const next = Number(raw);
    setDraft((prev) => (prev === null ? prev : { ...prev, [key]: Number.isNaN(next) ? 0 : next }));
  };

  const defaultEntries = Object.entries(defaults)
    .filter(([, value]) => value.trim() !== "")
    .map(([disciplineId, value]) => ({ disciplineId, targetPct: Number(value) }))
    .filter((d) => Number.isInteger(d.targetPct));

  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <h2 className="font-display text-lg font-semibold">Resource Rundown</h2>
        <p className="max-w-3xl text-sm text-muted-foreground">
          How the monthly Rundown classifies each Resource&apos;s utilization. Utilization is
          measured in hours, from NOS guidance and actuals — it is not the same thing as Capacity,
          which Staff measures in points.
        </p>
      </div>

      <section className="space-y-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-base font-semibold">Band thresholds</h3>
          <span className="text-xs text-muted-foreground">
            Currently on version {data.settings.version}
          </span>
        </div>
        <p className="max-w-3xl text-sm text-muted-foreground">
          Saving creates a new version. Editions already published keep the version they were built
          on, so their numbers never move.
        </p>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {THRESHOLD_FIELDS.map((field) => (
            <div key={field.key} className="space-y-1.5">
              <Label htmlFor={`rundown-${field.key}`} className="text-sm">
                {field.label}
                <span className="ml-1 text-xs font-normal text-muted-foreground">
                  ({field.unit})
                </span>
              </Label>
              <Input
                id={`rundown-${field.key}`}
                type="number"
                inputMode="numeric"
                value={String(draft[field.key])}
                onChange={(e) => onThresholdChange(field.key, e.target.value)}
              />
              <p className="text-xs text-muted-foreground">{field.hint}</p>
            </div>
          ))}
        </div>

        <div className="space-y-3 border-t pt-4">
          <h4 className="text-sm font-semibold">Availability colours</h4>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Where the Availability report changes colour, as a share of each month&apos;s Available
            Hours. Keep them in order: Some room, Fully booked, Stretched, then Over-utilized.
          </p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {AVAILABILITY_FIELDS.map((field) => (
              <div key={field.key} className="space-y-1.5">
                <Label htmlFor={`rundown-${field.key}`} className="text-sm">
                  {field.label}
                  <span className="ml-1 text-xs font-normal text-muted-foreground">
                    ({field.unit})
                  </span>
                </Label>
                <Input
                  id={`rundown-${field.key}`}
                  type="number"
                  inputMode="numeric"
                  value={String(draft[field.key])}
                  onChange={(e) => onThresholdChange(field.key, e.target.value)}
                />
                <p className="text-xs text-muted-foreground">{field.hint}</p>
              </div>
            ))}
          </div>
        </div>

        <Button onClick={() => saveSettings.mutate(draft)} disabled={saveSettings.isPending}>
          {saveSettings.isPending ? "Saving…" : "Save as new version"}
        </Button>
      </section>

      <NonDeliveryHomeRolesSection />

      <section className="space-y-4">
        <h3 className="text-base font-semibold">Billable Target defaults</h3>
        <p className="max-w-3xl text-sm text-muted-foreground">
          The target a Resource falls back to when they have none of their own. A Resource&apos;s
          own target changes only through an approved Target Adjustment — there is no direct edit.
          Leave a Discipline blank for no default.
        </p>

        {data.disciplineDefaults.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No Disciplines are configured yet, so there is nothing to set a default for.
          </p>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {data.disciplineDefaults.map((d) => (
                <div key={d.disciplineId} className="space-y-1.5">
                  <Label htmlFor={`rundown-default-${d.disciplineId}`} className="text-sm">
                    {d.disciplineName}
                    <span className="ml-1 text-xs font-normal text-muted-foreground">(%)</span>
                  </Label>
                  <Input
                    id={`rundown-default-${d.disciplineId}`}
                    type="number"
                    inputMode="numeric"
                    placeholder="None"
                    value={defaults[d.disciplineId] ?? ""}
                    onChange={(e) =>
                      setDefaults((prev) => ({ ...prev, [d.disciplineId]: e.target.value }))
                    }
                  />
                </div>
              ))}
            </div>

            <Button
              variant="outline"
              onClick={() => saveDefaults.mutate(defaultEntries)}
              disabled={saveDefaults.isPending}
            >
              {saveDefaults.isPending ? "Saving…" : "Save defaults"}
            </Button>
          </>
        )}
      </section>
    </div>
  );
}
